import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, styled, Card, CardActionArea, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { format as rutFormat } from "rut.js";
import { format } from "date-fns";
import { ObscuredText, TooltipComponent } from "@components";
import { useRequestSigningStatus } from "@hooks";
import {
  RequestStatus,
  RequestType,
  StatusPerRequestType,
  StatusTranslation,
  requestTypeSimplifiedTranslation,
} from "@interfaces";
import { Circle } from "@mui/icons-material";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "disableClick",
})<{ disableClick?: boolean }>(({ theme, disableClick = false }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: "4px",
  cursor: disableClick ? "default" : "pointer",
}));

const StyledCardActionArea = styled(CardActionArea, {
  shouldForwardProp: (prop) => prop !== "disableClick",
})<{ disableClick?: boolean }>(({ theme, disableClick = false }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: 8,
  gap: 8,
  color: theme.palette.background.paper,
  borderRadius: "4px",
  cursor: disableClick ? "default" : "pointer",
}));

const Content = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  width: "100%",
  flex: 1,
  alignItems: "center",
  overflow: "auto",
  backgroundColor: theme.palette.background.paper,
}));

const ContentHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  flex: 1,
  alignItems: "center",
});

const ContentDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  flex: 1,
  gap: "4px",
});

const Actions = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
  flex: 0,
  alignItems: "center",
});

const RequestCardType = styled(Typography, {
  shouldForwardProp: (prop: string) =>
    prop !== "highlightStatus" && prop !== "theme",
})<{ highlightStatus: boolean }>(({ theme, highlightStatus }) => ({
  fontSize: "12px",
  fontWeight: highlightStatus ? 500 : 400,
  color: highlightStatus
    ? theme.palette.text.primary
    : theme.palette.text.primary,
}));

const RequestCardTime = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "highlightStatus",
})<{ highlightStatus: boolean }>(({ highlightStatus, theme }) => ({
  fontSize: "12px",
  fontWeight: 400,
  textAlign: "right",
  color: highlightStatus
    ? theme.palette.text.primary
    : theme.palette.text.primary,
}));

const RequestCardPatient = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "highlightStatus",
})<{ highlightStatus: boolean }>(({ highlightStatus, theme }) => ({
  fontSize: "16px",
  fontWeight: highlightStatus ? 500 : 400,
  color: theme.palette.text.primary,
}));

const StatusDotPending = styled(Circle)(({ theme }) => ({
  width: "8px",
  height: "8px",
  color: theme.palette.primary.main,
}));

const StatusDotError = styled(Circle)(({ theme }) => ({
  width: "8px",
  height: "8px",
  color: theme.palette.error.main,
}));

const RequestCardDetails = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "highlightStatus",
})<{ highlightStatus: boolean }>(({ highlightStatus, theme }) => ({
  fontSize: "14px",
  lineHeight: "19px",
  fontWeight: highlightStatus ? 500 : 400,
  color: theme.palette.text.primary,
}));

const StyledRightArrow = styled(KeyboardArrowRightIcon)(({ theme }) => ({
  color: theme.palette.icon.primary,
}));

interface RequestCardProps {
  id: string;
  name: string;
  lastName: string;
  rut?: string;
  passport?: string;
  requestedAt: Date;
  status: RequestStatus;
  requestType: RequestType;
  isLoadingRequests: boolean;
  managementSummary?: string;
}
export function RequestCard({
  id,
  name,
  lastName,
  rut,
  passport,
  requestedAt,
  status,
  requestType,
  isLoadingRequests,
  managementSummary,
}: RequestCardProps) {
  const navigate = useNavigate();
  const { isSigningOrError, isSigning, isSigningError } =
    useRequestSigningStatus(status);
  const onCardClick = useCallback(() => {
    if (isLoadingRequests) return;
    const isRequestSigningOrError = isSigningOrError;
    if (isRequestSigningOrError) return;
    if (status === RequestStatus.PENDING)
      navigate(`/app/solicitudes/${id}/gestionar`);
    else navigate(`/app/solicitudes/${id}/resumen`, { state: { requestType } });
  }, [id, requestType, status, navigate, isSigningOrError, isLoadingRequests]);

  const parseRequestedAt = useCallback(() => {
    if (new Date().toDateString() === requestedAt.toDateString()) {
      return format(requestedAt, "HH:mm");
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (yesterday.toDateString() === requestedAt.toDateString()) {
      return "Ayer";
    }
    return format(requestedAt, "dd/MM");
  }, [requestedAt]);

  const isAccepted = status === RequestStatus.ACCEPTED;
  const isPending = status === RequestStatus.PENDING;
  const highlightStatus = isPending || isSigningOrError;
  const cardDetails = isPending
    ? rut
      ? rutFormat(rut)
      : passport
        ? passport
        : ""
    : (managementSummary ?? (rut ? rutFormat(rut) : passport ? passport : ""));
  return (
    <StyledCard
      elevation={0}
      onClick={onCardClick}
      disableClick={isSigningOrError || isLoadingRequests}
    >
      <StyledCardActionArea disableClick={isSigningOrError || isLoadingRequests}>
        <Content>
          <ContentHeader>
            <RequestCardType highlightStatus={highlightStatus}>
              {isAccepted
                ? `${requestTypeSimplifiedTranslation[requestType]} · ${StatusPerRequestType[requestType][status]}`
                : `${StatusTranslation[status]}`}
            </RequestCardType>
            <RequestCardTime highlightStatus={highlightStatus}>
              {parseRequestedAt()}
            </RequestCardTime>
          </ContentHeader>
          <ContentDetails>
            <ObscuredText
              component={RequestCardPatient}
              noWrap
              highlightStatus={highlightStatus}
            >
              {name} {lastName}
            </ObscuredText>
            <TooltipComponent title={cardDetails}>
              <RequestCardDetails noWrap highlightStatus={highlightStatus}>
                {cardDetails}
              </RequestCardDetails>
            </TooltipComponent>
          </ContentDetails>
        </Content>
        <Actions>
          {isPending || isSigning ? (
            <StatusDotPending />
          ) : isSigningError ? (
            <StatusDotError />
          ) : null}
          {!isSigningOrError && !isPending && <StyledRightArrow />}
        </Actions>
      </StyledCardActionArea>
    </StyledCard>
  );
}
